import { Injectable } from '@angular/core';
import { CapgainDistributionService } from '@products/services/capgain-distribution.service';
import {
  CapGainsEstimatesListing,
  CapGainListingFundStatus,
  CapGainsEstimatesState,
  CapGainsEstimatesFilterType,
  CapGainsEstimatesSelectedFilters,
  CapGainsEstimatesAction,
  CapGainsEstimatesRowSelection,
  CapGainsEstimatesFavoritesToggleState,
  CapGainsEstimatesColumnSortDetails,
  CapGainsEstimatesColumnId,
} from '@products/types/tax.type';
import { Logger } from '@utils/logger';
import { forkJoin, Observable, of, ReplaySubject, Subject } from 'rxjs';
import CapGainEstimatesQuery from '@graphql/tax/us-capital-gains-estimates.graphql';
import {
  FundIdentifier,
  PersonalisationFavoriteData,
  ProductTypes,
  SwitchState,
} from '@types';
import { CapGainDistribution } from '@products/models/capgain-distribution';
import { SiteConfigService } from '@services/site-config.service';
import cloneDeep from 'lodash/cloneDeep';
import sortedUniq from 'lodash/sortedUniq';
import { DropdownItem, PagedRecords } from '@frk/eds-components';
import { TranslateService } from '@shared/translate/translate.service';
import { NA, SINGLECLASS } from '@products/utils/constants/product.constants';
import dayjs from 'dayjs';
import { ActivatedRoute } from '@angular/router';
import {
  ExcelDownloadService,
  FtCellObject,
} from '@products/services/excel-download.service';
import { FavoritesService } from '@products/services/favorites.service';
import { ProfileService } from '@services';
import { switchMap } from 'rxjs/operators';

const logger = Logger.getLogger('UsCapitalGainsEstimatesService');

// ********** Page configurations start **********

// Listing page size.
const PAGE_SIZE = 100; // WDE-1426: Limit increased from 25 to 100.

// Filter(s) to be created.
// Item name must be equal to "field name" in 'CapGainEstimatesListing'.
const FILTER_TYPES: (keyof CapGainsEstimatesListing)[] = [
  'productType',
  'assetClass',
  'investmentGroup',
  'shareClassName',
];

// NGC-9135: Order of items for product type filter.
const PRODUCT_TYPE_ITEMS_ORDER: ProductTypes[] = [
  ProductTypes.MUTUAL_FUNDS,
  ProductTypes.ETFs,
  ProductTypes.TIF,
  ProductTypes.MONEY_MARKET_FUNDS,
  ProductTypes.INTERVAL_FUNDS,
  ProductTypes.CLOSED_END_FUNDS,
  ProductTypes.COLLECTIVE_INVESTMENT_TRUST,
];

// Restrict cap gains listing as per item(s) present in the 'PRODUCT_TYPE_ITEMS_ORDER'.
const RESTRICT_LISTING_BY_PRODUCT_TYPE_ITEMS = true;

// Searchable fields.
// Item name must be equal to "field name" in 'CapGainEstimatesListing'.
const SEARCHABLE_FIELDS: (keyof CapGainsEstimatesListing)[] = [
  'fundId',
  'fundName',
  'shareClassName',
  'cusip',
  'ticker',
  'taId',
];

// ********** Page configurations ends **********

@Injectable({
  providedIn: 'root',
})
export class UsCapitalGainsEstimatesService {
  private state: CapGainsEstimatesState = {
    showListing: null,
  };
  private capGainsEstimatesState$: ReplaySubject<CapGainsEstimatesState>;
  protected pagedRecords: PagedRecords<CapGainsEstimatesListing>;
  private capGainListing: CapGainsEstimatesListing[] = [];
  private originalcapGainListing: CapGainsEstimatesListing[] = [];
  private totalSelectedRows$: Subject<number>;
  private favoritesToggleState: CapGainsEstimatesFavoritesToggleState = {
    showFavoritesOnlySwitch: false,
  };
  private favoritesToggleState$: Subject<CapGainsEstimatesFavoritesToggleState>;

  private hideFundEstimateFlag$: ReplaySubject<boolean>;
  private hideFundEstimate = true;
  private selectedFilters: CapGainsEstimatesSelectedFilters;
  private totalRecords$: Subject<CapGainDistribution[]>;
  constructor(
    private capgainDistributionService: CapgainDistributionService,
    private siteConfigService: SiteConfigService,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private excelDownloadService: ExcelDownloadService,
    private profileService: ProfileService,
    private favoritesService: FavoritesService
  ) {
    this.capGainsEstimatesState$ = new ReplaySubject();
    this.pagedRecords = new PagedRecords<CapGainsEstimatesListing>(PAGE_SIZE);
    this.totalSelectedRows$ = new Subject<number>();
    this.favoritesToggleState$ = new Subject<CapGainsEstimatesFavoritesToggleState>();
    this.hideFundEstimateFlag$ = new ReplaySubject<boolean>();
    this.totalRecords$ = new Subject<CapGainDistribution[]>();
  }

  /**
   * Return the state updates as an observable.
   */
  public getCapGainsEstimatesState$(): Observable<CapGainsEstimatesState> {
    return this.capGainsEstimatesState$.asObservable();
  }

  /**
   * Fetch cap gains distributions feed and format it into our own feasible data structure.
   * Extract filter items from the feed and configure pagination stuff.
   * Finally publish the state accordingly.
   */
  public populate(): void {
    this.capgainDistributionService.register(CapGainEstimatesQuery).subscribe(
      (capGainDistributions) => {
        logger.debug('Cap Gains Feed: ', capGainDistributions);

        // Process the pds feed as required.
        this.totalRecords$.next(capGainDistributions);
        this.processState(capGainDistributions);
      },
      (error) => {
        logger.error('Error in fetching cap gains feed from PDS: ', error);

        // Publish erroneous state.
        this.capGainsEstimatesState$.error(error);
      }
    );
  }

  /**
   * Process the business logics required for cap gains listing.
   * Further, format the final listing to required data structure and extract the filters.
   * Finally publish the state accordingly.
   * @param capGainDistributions cap gains full pds feed
   */
  private processState(capGainDistributions: CapGainDistribution[]): void {
    // Enclose all processing within error handling.
    try {
      // Step 1: Firstly, if 'flag' true, filter out the listing as per product type items present in 'PRODUCT_TYPE_ITEMS_ORDER'.
      if (RESTRICT_LISTING_BY_PRODUCT_TYPE_ITEMS) {
        capGainDistributions = capGainDistributions.filter((capGainDistObj) =>
          PRODUCT_TYPE_ITEMS_ORDER.includes(
            capGainDistObj.productType as ProductTypes // Intentionally, type casted to match both. It would be same anyways.
          )
        );
      }

      // Step 2: Include only those records, for which we have 'capitalGainRecordDate' for current year.
      capGainDistributions = capGainDistributions.filter(
        (capGainDistObj) => {
          return (
            capGainDistObj.capitalGainRecordDate &&
            dayjs(capGainDistObj.capitalGainRecordDate).year() ===
              dayjs().year()
          );
        }
        // Intentionally evaluated 'capitalGainRecordDate', as 'undefined' record dates would be passed in next condition.
      );

      // Step 3: Configure "forced view" of listing/search box through URL query param.
      //         Extract 'toggleView' query param value from the current route.
      const toggleViewValue: string = this.activatedRoute.snapshot.queryParams
        .toggleView;

      // Check if, query param has 'grid' value.
      // If yes, show listing page.
      if (toggleViewValue === 'grid') {
        this.state.showListing = true;
      }
      // Else if, check query param has 'search' value.
      // If yes, show search box.
      else if (toggleViewValue === 'search') {
        this.state.showListing = false;
      }
      // For no value/any other value, make a normal flow.
      // So, check if, current month is either nov. or dec. and there is an estimate for any record in the new listing.
      // If yes, show listing, else search box.
      else {
        this.state.showListing =
          [10, 11].includes(dayjs().month()) &&
          capGainDistributions.some(
            (capGainDistObj) => capGainDistObj.estimateFlag === 'true'
          );
      }

      // Step 4: Process all the grid related tasks, if flag is true.
      if (this.state.showListing) {
        // Format the pds feed into feasible data structure.
        // NGC-9841: Filter out funds based upon status.
        // Hence, remove "inactive funds", while other 'status' will be handled in 'template' accordingly.
        // Also, filter the final displayable records by most recent distribution data for each fund share class.

        /**
         *  DCE-2334
         * 1. Active : Show in grid and take user to product page
         * 2. Soft Launch : Show in Grid but disable product page link
         * 3. Inactive: Do not show in grid
         * 4. Soft Closed: Do not show in grid
         */
        this.capGainListing = this.formatCapGainDistributionsData(
          this.filterRecentRecords(
            capGainDistributions.filter(
              (capGainDistribution) =>
                // WDE-3374: Include only active and soft-launch funds.
                capGainDistribution.fundStatus ===
                  CapGainListingFundStatus.active ||
                // WDE-3374: For soft-launch funds, check if they don't have actuals distributed.
                (capGainDistribution.fundStatus ===
                  CapGainListingFundStatus['soft-launch'] &&
                  // Below condition satisfies fund having actual distribution data.
                  !(
                    capGainDistribution.estimateFlag === 'false' &&
                    Boolean(capGainDistribution.totalDistributionAmountMonthly)
                  ))
            )
          )
        );

        this.originalcapGainListing = this.capGainListing;

        // Extract filter(s) data from the listing.
        this.state.filters = this.extractFiltersData(
          this.capGainListing,
          FILTER_TYPES
        );
        if (this.hideFundEstimate) {
          this.capGainListing = this.getFundEstimates(this.capGainListing);
        }
        // Also, assign the record details.
        // Initially, 'total' & 'filtered' records would be same with default "page size".
        this.state.recordDetails = {
          total: this.originalcapGainListing.length,
          filtered: this.capGainListing.length,
          pageSize: PAGE_SIZE,
        };

        // Further, configure pagination to display listing in ag-grid table accordingly.
        this.pagedRecords.records = cloneDeep(this.capGainListing);
      }

      // Step 5: Finally, publish the updated state.
      this.state.action = CapGainsEstimatesAction.INITIAL_LOAD;
      this.state.isLoaded = true;
      this.capGainsEstimatesState$.next(this.state);
    } catch (exception) {
      logger.error('Error in processing feed and assigning state: ', exception);

      // Publish incomplete state.
      this.state.isLoaded = false;
      this.capGainsEstimatesState$.next(this.state);
    }
  }

  /**
   * Extract the most recent distribution record for each fund share class.
   * @param capGainDistributions filtered cap gains pds feed
   */
  private filterRecentRecords(
    capGainDistributions: CapGainDistribution[]
  ): CapGainDistribution[] {
    const recentRecordDistributions: CapGainDistribution[] = [];

    // Loop each cap gain distribution record to find out duplicate ones.
    capGainDistributions.forEach((capGainDistribution) => {
      // Push the first record since the recent record list will be empty at first time.
      if (!recentRecordDistributions.length) {
        recentRecordDistributions.push(capGainDistribution);
      } else {
        // Extract the matched record index in recent list.
        const matchedRecentRecordIndex: number = recentRecordDistributions.findIndex(
          (recentRecordDistribution) =>
            `${recentRecordDistribution.fundId}-${recentRecordDistribution.shareClassCode}` ===
            `${capGainDistribution.fundId}-${capGainDistribution.shareClassCode}`
        );

        // If index is defined as a valid one.
        if (matchedRecentRecordIndex !== -1) {
          // Then check if the new record is a date 'after' than a matched one.
          if (
            dayjs(capGainDistribution.capitalGainRecordDate).isAfter(
              recentRecordDistributions[matchedRecentRecordIndex]
                .capitalGainRecordDate
            )
          ) {
            // If yes, replace the old record with the new one.
            recentRecordDistributions.splice(
              matchedRecentRecordIndex,
              1,
              capGainDistribution
            );
          }
        }
        // If invalid index, then push the new record into the list.
        else {
          recentRecordDistributions.push(capGainDistribution);
        }
      }
    });

    // Finally, return the filtered list.
    return recentRecordDistributions;
  }

  /**
   * Format the pds feed into feasible data structure.
   * And, sort the same alphabetically against fund name and share class name.
   * @param capGainDistributions cap gain distributions feed
   * @returns sorted cap gains estimates list
   */
  private formatCapGainDistributionsData(
    capGainDistributions: CapGainDistribution[]
  ): CapGainsEstimatesListing[] {
    return this.sortCapGainListing(
      capGainDistributions.map((capGainDistObj) => {
        // Extract default identifier key against taxonomy for current object.
        const defaultIdentifierKey = this.siteConfigService.getDefaultIdentifierKey(
          capGainDistObj.productTaxonomy
        );

        // Search for 'key' as 'field' in current object, and retrieve its value.
        const defaultIdentifier: FundIdentifier = defaultIdentifierKey
          ? (capGainDistObj[defaultIdentifierKey] as FundIdentifier)
          : null;

        // Assign and form the expected data structure.
        return {
          fundId: capGainDistObj.fundId,
          fundName: capGainDistObj.fundName,
          fundLink:
            // WDE-3374: Restrict the link creation for soft launch funds.
            capGainDistObj.fundStatus !==
            CapGainListingFundStatus['soft-launch']
              ? `${this.siteConfigService.getFundLink(
                  capGainDistObj.fundId,
                  capGainDistObj.shareClassCode,
                  capGainDistObj.fundName,
                  defaultIdentifier
                )}#distributions`
              : null,
          fundStatus: capGainDistObj.fundStatus,
          shareClassCode: capGainDistObj.shareClassCode,
          shareClassName: capGainDistObj.shareClassName,
          isPrimaryShareClass: capGainDistObj.primaryShareClassIndicator,
          productType: capGainDistObj.productType,
          assetClass: capGainDistObj.assetClass,
          cusip: capGainDistObj.cusip,
          ticker: capGainDistObj.ticker,
          taId: capGainDistObj.taId,
          investmentGroup: capGainDistObj.investmentGroup,
          capitalGainRecordDate: capGainDistObj.capitalGainRecordDateFormatted, // Save formatted date for display.
          capitalGainPayableDate:
            capGainDistObj.capitalGainPayableDateFormatted, // Save formatted date for display.
          longTermCapitalGainAmount: capGainDistObj.longTermCapitalGainAmount,
          shortTermCapitalGainAmount: capGainDistObj.shortTermCapitalGainAmount,
          totalDistributionAmountMonthly:
            capGainDistObj.totalDistributionAmountMonthly,
          estimateFlag: capGainDistObj.estimateFlag === 'true',
          // Show 'actuals' and its 'alert' when 'estimate' is 'false' with valid capgain record (say, 'totalDistributionAmountMonthly').
          actualsAlert:
            capGainDistObj.estimateFlag === 'false' &&
            Boolean(capGainDistObj.totalDistributionAmountMonthly),
          isSelected: false, // By default, no row is required to be selected.
          navValue: capGainDistObj.navValue,
          capitalGainExDividendDate: capGainDistObj.capitalGainExDividendDate,
          longTermCapitalGainPercent: capGainDistObj.longTermCapitalGainPercent,
          shortTermCapitalGainPercent:
            capGainDistObj.shortTermCapitalGainPercent,
          totalGainNav: capGainDistObj.totalGainNav,
          navAsofDate: capGainDistObj.navAsofDate,
        };
      })
    );
  }

  /**
   * Returns the sorted estimates listing against fund name and share class name.
   * @param capGainListing unformatted cap gain listing.
   */
  private sortCapGainListing(
    capGainListing: CapGainsEstimatesListing[]
  ): CapGainsEstimatesListing[] {
    return capGainListing.sort((prev, next) => {
      const fundNameCompVal: number = prev.fundName.localeCompare(
        next.fundName
      );

      // If fund name seems to be same, further check against share class name.
      if (fundNameCompVal === 0) {
        return prev.shareClassName.localeCompare(next.shareClassName);
      }

      return fundNameCompVal;
    });
  }

  /**
   * Extract the filters data as needed.
   * @param capGainListing formatted cap gain listing.
   * @param filterTypes filter type list
   * @returns list of filter type(s) with their item(s)
   */
  private extractFiltersData(
    capGainListing: CapGainsEstimatesListing[],
    filterTypes: string[]
  ): CapGainsEstimatesFilterType[] {
    const capGainFilterTypes: CapGainsEstimatesFilterType[] = [];

    // 1. Loop through each filter type listed.
    filterTypes.forEach((filterType) => {
      // 2. Extract the unique values against the filter type above.
      //    Example, all product types values like, MF/ETF for the field 'productType'.
      //    So, first extract all values, sort them and then extract non-duplicate values.
      const filterTypeUniqueItems: string[] = sortedUniq(
        capGainListing
          .map((capGainListingObj) => capGainListingObj[filterType])
          .sort((prev: string, next: string) => prev.localeCompare(next))
      );

      // 3. Now, remove 'SINGLCLASS' & 'NA' from shareClassName filter items, if present.
      //    First, convert all elements in lowercase and save as formatted list.
      //    Then, fetch index against the matched one and splice it from the array.
      //    ********** Not needed for now, but, can be kept for future, if BA wants to add.
      //    This will be a mandatory condition, if Share Class Name filter included at later stage.
      //    Hence keeping this as a condition, so that we shouldn't skip the same. **********
      if (filterType === 'shareClassName') {
        [SINGLECLASS.toLowerCase(), NA.toLowerCase()].forEach(
          (shareClassName) => {
            const formattedFilterTypeUniqueItems = filterTypeUniqueItems.map(
              (filterTypeUniqueItem: string) =>
                filterTypeUniqueItem.toLowerCase()
            );
            const index = formattedFilterTypeUniqueItems.indexOf(
              shareClassName
            );

            if (index !== -1) {
              filterTypeUniqueItems.splice(index, 1);
            }
          }
        );
      }

      // 4. Create the items list by mapping different required field values.
      let capGainFilterTypeItems: DropdownItem[] = [];
      filterTypeUniqueItems.map((uniqueItem) => {
        capGainFilterTypeItems.push({
          label:
            filterType === 'productType'
              ? this.translateService.instant(
                  `products.capgains-filter-producttype-${uniqueItem
                    .toLowerCase()
                    .split(' ')
                    .join('-')}`
                )
              : uniqueItem,
          value: uniqueItem,
          checked: false,
        });
      });

      // 5. Sort again, for 'filterType' as 'productType'.
      //    As per the order defined manually.
      if (filterType === 'productType') {
        const sortedFilterTypeItems: DropdownItem[] = [];

        // ***** NGC-9135: Made product type filter items to be order as defined manually. *****
        PRODUCT_TYPE_ITEMS_ORDER.forEach((productTypeItem) => {
          const capGainFilterTypeItem = capGainFilterTypeItems.find(
            (item) => item.value === productTypeItem
          );

          if (capGainFilterTypeItem) {
            sortedFilterTypeItems.push(capGainFilterTypeItem);
          }
        });

        // Fallback for "product types" present in 'PDS feed' and not in 'PRODUCT_TYPE_ITEMS_ORDER'.
        if (sortedFilterTypeItems.length !== capGainFilterTypeItems.length) {
          // Loop all "product types" present in pds feed.
          capGainFilterTypeItems.forEach((item) => {
            if (
              // The one which is not found in sorted one, is the item to be inserted at last.
              // As its priority isn't defined in 'PRODUCT_TYPE_ITEMS_ORDER'.
              !sortedFilterTypeItems.find(
                (sortedItem) => sortedItem.value === item.value
              )
            ) {
              sortedFilterTypeItems.push(item);
            }
          });
        }

        capGainFilterTypeItems = sortedFilterTypeItems;
      }

      // 6. Finally, push the created filter type object to the list.
      capGainFilterTypes.push({
        filterLabel: filterType.toLowerCase(), // Intentionally made lower case, to use it as filter label in kebab-case.
        filterName: filterType,
        filterItems: capGainFilterTypeItems,
      });
    });

    return capGainFilterTypes;
  }

  /**
   * ******************** Sort column methods ********************
   */

  /**
   * Sort the current grid as per the column details provided.
   * @param colSortDetails details of the column to be sorted
   */
  public handleGridSort(
    colSortDetails: CapGainsEstimatesColumnSortDetails
  ): void {
    colSortDetails.sortType === 'asc'
      ? this.pagedRecords.records.sort((first, second) =>
          this.processColumnComparison(first, second, colSortDetails.columnId)
        )
      : this.pagedRecords.records.sort(
          (first, second) =>
            // Negate the value in case of 'desc'.
            -this.processColumnComparison(
              first,
              second,
              colSortDetails.columnId
            )
        );

    // Save the changed reference to publish the sorted listing to pagination library.
    // This will further update all relevant subscriptions.
    this.pagedRecords.records = cloneDeep(this.pagedRecords.records);
  }

  /**
   * Handle the comparison of two records based upon column and its type.
   * Upon successful comparison return final numeral value.
   * @param first first record
   * @param second second record
   * @param colId column id to be sorted
   * @returns compared value as number (0, 1 or -1)
   */
  private processColumnComparison(
    first: CapGainsEstimatesListing,
    second: CapGainsEstimatesListing,
    colId: CapGainsEstimatesColumnId
  ): number {
    switch (colId) {
      case CapGainsEstimatesColumnId.RECORD_DATE:
        return this.getCompareValueByType(
          first,
          second,
          'capitalGainRecordDate',
          'date'
        );
      case CapGainsEstimatesColumnId.EX_DATE:
        return this.getCompareValueByType(
          first,
          second,
          'capitalGainExDividendDate',
          'date'
        );
      case CapGainsEstimatesColumnId.LONG_TERM_CAP_GAINS:
        return this.getCompareValueByType(
          first,
          second,
          'longTermCapitalGainAmount',
          'price'
        );
      case CapGainsEstimatesColumnId.TOTAL_PERCENT:
        return this.getCompareValueByType(
          first,
          second,
          'totalGainNav',
          'price'
        );
      case CapGainsEstimatesColumnId.SHORT_TERM_CAP_GAINS:
        return this.getCompareValueByType(
          first,
          second,
          'shortTermCapitalGainAmount',
          'price'
        );
      case CapGainsEstimatesColumnId.SHORT_TERM_CAP_GAINS_PERCENT:
        return this.getCompareValueByType(
          first,
          second,
          'shortTermCapitalGainPercent',
          'price'
        );
      case CapGainsEstimatesColumnId.LONG_TERM_CAP_GAINS_PERCENT:
        return this.getCompareValueByType(
          first,
          second,
          'longTermCapitalGainPercent',
          'price'
        );
      case CapGainsEstimatesColumnId.TOTAL_CAP_GAINS:
        return this.getCompareValueByType(
          first,
          second,
          'totalDistributionAmountMonthly',
          'price'
        );
      case CapGainsEstimatesColumnId.NAV_AS_OF:
        return this.getCompareValueByType(first, second, 'navValue', 'price');
      default:
        return first.fundName.localeCompare(second.fundName);
    }
  }

  /**
   * Compare the two records to be sorted as per the column data type and return the numeral value.
   * @param first first record
   * @param second second record
   * @param column column (field) to be compared
   * @param type column data type - date or estimated price range
   */
  private getCompareValueByType(
    first: CapGainsEstimatesListing,
    second: CapGainsEstimatesListing,
    column: keyof CapGainsEstimatesListing,
    type: 'date' | 'price'
  ): number {
    // Step 1: First check, if it is a 'price' column. For any 'actuals' record, make it to the last.
    if (type === 'price') {
      if (first.actualsAlert) {
        return 1;
      } else if (second.actualsAlert) {
        return -1;
      }
    }

    // Step 2: If not 'price' and 'actuals', for any column, if two records matches,
    //         Then as a fallback, compare both with its "fund name."
    if (first[column] === second[column]) {
      return first.fundName.localeCompare(second.fundName);
    }

    // Step 3: If both not same, then for 'date' type column, compare the dates through dayjs.
    if (type === 'date') {
      return dayjs(first[column] as string, 'MM/DD/YYYY').isAfter(
        dayjs(second[column] as string, 'MM/DD/YYYY')
      )
        ? 1
        : -1;
    }
    // Step 4: Else, for 'price' type column, firstly, extract the 'first' part of estimated range.
    else {
      const firstRecordFirstPart: number = this.getPartOfEstimatedRangeInNumber(
        first[column] as string,
        'first'
      );
      const secondRecordFirstPart: number = this.getPartOfEstimatedRangeInNumber(
        second[column] as string,
        'first'
      );

      // Step 5: Compare both, if matches then, extract and compare 'second' part of estimated range.
      if (firstRecordFirstPart === secondRecordFirstPart) {
        return this.getPartOfEstimatedRangeInNumber(
          first[column] as string,
          'second'
        ) >
          this.getPartOfEstimatedRangeInNumber(
            second[column] as string,
            'second'
          )
          ? 1
          : -1;
      }

      // Step 6: Else, if different, compare the result and return.
      return firstRecordFirstPart > secondRecordFirstPart ? 1 : -1;
    }
  }

  /**
   * Extract first or second part of estimated price range in string and convert it into numeral value.
   * @param estimate estimated price string value
   * @param part part of estimated price range to be extracted - first or second
   * @returns numeral value
   */
  private getPartOfEstimatedRangeInNumber(
    estimate: string,
    part: 'first' | 'second'
  ): number {
    const index: number = part === 'first' ? 0 : 1;

    return Number(
      estimate.split('-')[index]?.replace('$', '').replace('%', '')
    );
  }

  /**
   * ******************** Favorite toggle methods ********************
   */

  /**
   * Return the favorite toggle state updates as an observable.
   */
  public getFavoritesToggleState$(): Observable<CapGainsEstimatesFavoritesToggleState> {
    return this.favoritesToggleState$.asObservable();
  }

  /**
   * Handle favorite toggle visibility and its state.
   * Load favorite funds and save their info in original listing with matching fund share class id.
   */
  public handleFavoriteToggle(): void {
    // Initialize favorites only state.
    // Check if user is logged in along with a truthy flag from BR config.
    this.profileService
      .isLoggedIn$()
      .pipe(
        switchMap((isLoggedIn) =>
          isLoggedIn && this.siteConfigService.isFavoriteFundsEnabled()
            ? this.getFavoriteList$()
            : of(null)
        )
      )
      .subscribe(
        (favoriteFunds: PersonalisationFavoriteData[]) => {
          logger.debug('Favorite fund share class ids: ', favoriteFunds);

          if (favoriteFunds) {
            if (favoriteFunds?.length) {
              // Save the favorite fund info for respective record in original listing for any favorite 'FundShareClassId' found.
              favoriteFunds.forEach((fav) => {
                const matchedFundIndex: number = this.originalcapGainListing.findIndex(
                  (capGainEstimate) =>
                    `${capGainEstimate.fundId}-${capGainEstimate.shareClassCode}` ===
                    fav.fundShareClassId
                );

                if (matchedFundIndex !== -1) {
                  this.originalcapGainListing[
                    matchedFundIndex
                  ].isFavorite = true;
                }
              });
            }

            // Assign the default values only on first valid load.
            if (!this.favoritesToggleState.favoritesOnlySwitchState) {
              this.favoritesToggleState.showFavoritesOnlySwitch = true;
              this.favoritesToggleState.favoritesOnlySwitchState =
                SwitchState.OFF;
            }

            // Also, trigger the grid filter listing, if toggle is in 'on' state.
            // This would be the case, when, user has made an action on toggle button and favorite list is loaded late.
            if (
              this.favoritesToggleState.favoritesOnlySwitchState ===
              SwitchState.ON
            ) {
              // Pass the 'selectedFilters' current state to apply it, if any, by the time favorite fund list is available.
              this.filterListing(this.selectedFilters, true);
            }
          }

          // Post update in original listing, if any, its ready for toggle action.
          // If not a valid share class id(s), load the default visibility as false.
          this.favoritesToggleState$.next(this.favoritesToggleState);
        },
        (error) => {
          logger.error('Error in loading favorites: ', error);
          this.favoritesToggleState$.next(this.favoritesToggleState);
        }
      );
  }

  /**
   * Return the favorite "fund share class ids" as an observable.
   */
  private getFavoriteList$(): Observable<PersonalisationFavoriteData[]> {
    return this.favoritesService.getFavorites$();
  }
  /**
   * this methods sets the flag to enable hide of funds with no estimates
   * @param flag it sets the value received from toggle to hide funds with no estimates
   */
  public setHideFundEstimateFlag$(flag: boolean) {
    this.hideFundEstimate = flag;
    this.hideFundEstimateFlag$.next(flag);
  }
  /**
   * this methods retrieves the flag to enable hide of funds with no estimates
   * @returns provides the status of of fund without estimates to show or hide
   */
  public getHideFundEstimateFlag$(): Observable<boolean> {
    return this.hideFundEstimateFlag$.asObservable();
  }

  /**
   * ******************** Grid filter method(s) ********************
   */

  /**
   * Filter the listing as per selected filters and update the subscriptions.
   * @param selectedFilters selected dropdown or searched text
   * @param isFavoritesToggleActive flag value specifying if favorite toggle has state 'on'
   */
  public filterListing(
    selectedFilters: CapGainsEstimatesSelectedFilters,
    isFavoritesToggleActive: boolean,
    hideNoEstimateFunds?: boolean
  ): void {
    const favoritesOnlyListing: CapGainsEstimatesListing[] = [];
    let filteredListing: CapGainsEstimatesListing[] = this
      .originalcapGainListing;

    // Save the current toggle & filter(s) state as it is needed to persist the same,
    // When favorite fund list is available so late that user has already made action on toggle.
    this.favoritesToggleState.favoritesOnlySwitchState = isFavoritesToggleActive
      ? SwitchState.ON
      : SwitchState.OFF;
    // And, might have selected filter(s) too.
    this.selectedFilters = selectedFilters;
    this.setHideFundEstimateFlag$(hideNoEstimateFunds);
    // Filter the new 'filteredListing' (and  capgain listing accordingly) based upon search input, if present.
    if (selectedFilters?.searchedText) {
      filteredListing = this.filterListWithSearchText(selectedFilters);
    }
    if (this.hideFundEstimate) {
      filteredListing = this.getFundEstimates(filteredListing);
    }
    // Check if favorite toggle state is 'on'.
    // If yes, make a separate listing with only favorite funds.
    // This will be used to have dropdown and search filters.
    if (isFavoritesToggleActive) {
      // Intentionally done cloneDeep to avoid any reference updates.
      filteredListing = cloneDeep(
        filteredListing.filter((capGainEstimate) => capGainEstimate.isFavorite)
      );
    }
    // Iterate with selected dropdown 'items', and, use 'some' to return as 'true' as soon as any 'item' is found.
    const isAnyFilterDropdownItemSelected: boolean = selectedFilters?.selectedDropdowns.some(
      (selectedDropdown) =>
        selectedDropdown.filterItems.map(
          (filterItem) => filterItem.value as string
        ).length
    );

    // Check if any filter item or search text is present.
    // If yes, proceed with grid listing filter.
    // Else, return the 'primary share class' or 'favoritesOnly' listing.

    if (isAnyFilterDropdownItemSelected) {
      selectedFilters.selectedDropdowns.forEach((selectedDropdown) => {
        const selectedDropdownItems: string[] = selectedDropdown.filterItems.map(
          (filterItem) => filterItem.value as string
        );

        if (selectedDropdownItems.length) {
          filteredListing = filteredListing.filter((capGainEstimate) =>
            selectedDropdownItems.includes(
              capGainEstimate[selectedDropdown.filterName]
            )
          );
        }
      });
    }

    // Update the filtered record length and action accordingly.
    this.state.recordDetails.filtered = filteredListing.length;
    this.state.recordDetails.total = this.originalcapGainListing.length;
    this.state.action = CapGainsEstimatesAction.FILTER_ROWS;

    // Publish the updated state.
    this.capGainsEstimatesState$.next(this.state);

    // Also, publish the filtered listing to pagination library.
    // This will further update all relevant subscriptions.
    this.pagedRecords.records = filteredListing;
    this.capGainListing = filteredListing;
  }
  /**
   *
   * @param selectedFilters the filters that are selected in componnet to filter funds
   * @returns filtered data w.r.t selectedFilters
   */
  private filterListWithSearchText(selectedFilters) {
    return this.sortCapGainListing(
      this.originalcapGainListing
    ).filter((capGainEstimate) =>
      SEARCHABLE_FIELDS.some((field) =>
        this.isSearchTermMatched(
          capGainEstimate,
          field,
          selectedFilters.searchedText.toLowerCase()
        )
      )
    );
  }
  /**
   * @returns the funds whose  longTermCapitalGainAmount
   * longTermCapitalGainPercent
   * shortTermCapitalGainAmount
   * shortTermCapitalGainPercent
   * totalGainNav values are > 0
   * @param capGainListing itis the complete funds for cap gains
   */
  getFundEstimates(
    capGainListing: CapGainsEstimatesListing[]
  ): CapGainsEstimatesListing[] {
    return cloneDeep(
      capGainListing.filter((capGainEstimate) => {
        const {
          longTermCapitalGainAmount,
          longTermCapitalGainPercent,
          shortTermCapitalGainAmount,
          shortTermCapitalGainPercent,
          totalGainNav,
        } = capGainEstimate;
        if (
          Number(parseFloat(longTermCapitalGainAmount.replace('$', ''))) > 0 ||
          Number(parseFloat(longTermCapitalGainPercent.replace('%', ''))) > 0 ||
          Number(parseFloat(shortTermCapitalGainAmount.replace('$', ''))) > 0 ||
          Number(parseFloat(shortTermCapitalGainPercent.replace('%', ''))) >
            0 ||
          Number(parseFloat(totalGainNav.replace('%', ''))) > 0
        ) {
          return capGainEstimate;
        }
      })
    );
  }
  /**
   * Handle the conditional check of fields against the search term.
   * @param capGainEstimate estimate record
   * @param searchableField field to be searched in estimate record
   * @param searchedText text to be matched in the searched field
   * @returns boolean flag if search term is matched
   */
  private isSearchTermMatched(
    capGainEstimate: CapGainsEstimatesListing,
    searchableField: keyof CapGainsEstimatesListing,
    searchedText: string
  ): boolean {
    switch (searchableField) {
      case 'fundId':
        return (capGainEstimate[searchableField] as string)
          .toLowerCase()
          .includes(searchedText);
      case 'fundName':
        return capGainEstimate[searchableField]
          .toLowerCase()
          .includes(searchedText);
      case 'shareClassName':
        return capGainEstimate[searchableField].toLowerCase() === searchedText;
      default:
        return (capGainEstimate[searchableField] as string)
          .toLowerCase()
          .includes(searchedText);
    }
  }

  /**
   * ******************** Row selection/deselection methods ********************
   */

  /**
   * Handle the selection/deselection of the fund row(s).
   * @param rowSelection 'flag' value with record 'id'
   */
  public updateRowSelection(rowSelection: CapGainsEstimatesRowSelection): void {
    // Check if, specific fund share class id is present.
    if (rowSelection.selectedFundShareClassId) {
      // Save the selection state for specific record in grid and original listing.
      this.getCurrentGridRecords().find(
        (gridRecord) =>
          `${gridRecord.fundId}-${gridRecord.shareClassCode}` ===
          rowSelection.selectedFundShareClassId
      ).isSelected = rowSelection.isSelected;

      this.originalcapGainListing.find(
        (capGainEstimate) =>
          `${capGainEstimate.fundId}-${capGainEstimate.shareClassCode}` ===
          rowSelection.selectedFundShareClassId
      ).isSelected = rowSelection.isSelected;
    } else {
      // Fetch and save the current page number of the grid.
      const currentPageNumber: number = this.getCurrentPageNumber();

      // Save the selection state for all records in grid and original listing.
      this.getCurrentGridRecords().forEach((gridRecord) => {
        gridRecord.isSelected = rowSelection.isSelected;

        this.originalcapGainListing.find(
          (capGainEstimate) =>
            `${capGainEstimate.fundId}-${capGainEstimate.shareClassCode}` ===
            `${gridRecord.fundId}-${gridRecord.shareClassCode}`
        ).isSelected = rowSelection.isSelected;
      });

      // Update the paged records with the new selection 'flag' value.
      // And, go to the current page number. This is needed as resetting the records will move the grid to page one.
      // Also, it is required as select all won't update the checkbox selection automatically for all rows until change detection done.
      this.pagedRecords.records = this.getCurrentGridRecords();
      this.goToPage(currentPageNumber);
    }

    // Finally, publish the total number of rows selected in original listing, as this would have selection irrespective of filters.
    this.totalSelectedRows$.next(
      this.originalcapGainListing.filter(
        (capGainEstimate) => capGainEstimate.isSelected
      ).length
    );
  }

  /**
   *
   * @param year takes current year
   * @param month passing 10th (October)
   * @returns date + month + year
   */
  public getLastBusinessDay(year, month): string {
    // Get the last day of the month
    const date = new Date(year, month, 0);

    // Check if the last day is a weekend (Saturday or Sunday)
    while (date.getDay() === 0 || date.getDay() === 6) {
      // If it is, subtract one day
      date.setDate(date.getDate() - 1);
    }

    return (
      Number(date.getMonth() + 1) +
      '/' +
      date.getDate() +
      '/' +
      date.getFullYear()
    );
  }

  /**
   * Return selected rows count as observable.
   */
  public getTotalSelectedRows$(): Observable<number> {
    return this.totalSelectedRows$.asObservable();
  }

  /**
   * ******************** Download grid data methods ********************
   */

  /**
   * Handle download action events.
   * Process excel headers, body and caveats.
   * Finally export the grid data to excel.
   * @param isSelectiveDownload flag to be passed, if download action corresponds to selective rows
   */
  public handleDownloadAction(isSelectiveDownload = false): void {
    // Extract all the records required to be downloaded to excel.
    // Use 'selection' in case of inidvidual download, while only "primary share class" ones in case of all.
    const downloadableRecords: CapGainsEstimatesListing[] = isSelectiveDownload
      ? this.originalcapGainListing.filter(
          (capGainEstimate) => capGainEstimate.isSelected
        )
      : cloneDeep(this.originalcapGainListing);

    // Get fields required in excel
    const requiredDownloadableFields: string[][] = downloadableRecords?.map(
      (record) => {
        return [
          record.fundName,
          record.ticker,
          record.shareClassName,
          record.longTermCapitalGainPercent,
          record.shortTermCapitalGainPercent,
          record.totalGainNav,
          record.longTermCapitalGainAmount,
          record.shortTermCapitalGainAmount,
          record.navValue,
          record.capitalGainRecordDate,
          record.capitalGainExDividendDate,
          record.capitalGainPayableDate,
        ];
      }
    );

    // Column Header Text
    const dataHeaders = [
      this.translateService.instant(
        'products.capgains-excel-headers-fund-name'
      ),
      this.translateService.instant('products.capgains-table-heading-ticker'),
      this.translateService.instant(
        'products.capgains-table-heading-share-class'
      ),

      this.translateService.instant(
        'products.capgains-table-heading-long-term-cap-gains-perc'
      ),
      this.translateService.instant(
        'products.capgains-table-heading-short-term-cap-gains-perc'
      ),
      this.translateService.instant(
        'products.capgains-table-heading-total-percent'
      ),
      this.translateService.instant(
        'products.capgains-excel-headers-long-term-gain'
      ) +
        ' ' +
        this.translateService.instant(
          'products.capgains-excel-headers-cap-gains'
        ),
      this.translateService.instant(
        'products.capgains-excel-headers-short-term-gain'
      ) +
        ' ' +
        this.translateService.instant(
          'products.capgains-excel-headers-cap-gains'
        ),
      this.translateService.instant('products.NAV_VALUE') +
        ' ' +
        this.translateService.instant(
          'products.capgains-table-heading-nav-as-of'
        ) +
        ' ' +
        this.getLastBusinessDay(dayjs().year(), 10),
      this.translateService.instant(
        'products.capgains-excel-headers-record-date'
      ),
      this.translateService.instant('products.capgains-table-heading-ex-date'),
      this.translateService.instant(
        'products.capgains-table-heading-payable-date'
      ),
    ];
    // Add headers to fullData
    requiredDownloadableFields.unshift(dataHeaders);

    // Get Caveat and pass it to excel data
    forkJoin([
      this.excelDownloadService.getProximalCaveat$(
        'CapGainsExcelProximalBottom'
      ),
      this.excelDownloadService.getProximalCaveat$('CapGainsExcelProximalTop'),
    ]).subscribe((data) => {
      const [bottomCaveat, topCaveat]: [
        FtCellObject[][],
        FtCellObject[][]
      ] = data;
      this.exportToExcel(
        requiredDownloadableFields,
        topCaveat,

        bottomCaveat
      );
      this.capGainListing.forEach((list) => (list.isSelected = false));
      this.pagedRecords.records = this.capGainListing;
      this.totalSelectedRows$.next(0);
      this.originalcapGainListing.forEach(
        (capGainEstimate) => (capGainEstimate.isSelected = false)
      );
    });
  }

  /**
   * Export the table data to excel and auto download.
   * @param fullData full record for excel
   */
  private exportToExcel(
    fullData?: string[][],
    topCaveat?: FtCellObject[][],

    bottomCaveat?: FtCellObject[][]
  ): void {
    import('xlsx').then((xlsx) => {
      // Create new worksheet with topCaveat
      const workSheet = xlsx.utils.aoa_to_sheet(topCaveat);
      const workBook = xlsx.utils.book_new();

      // Feet to columns
      workSheet['!cols'] = this.fitToColumn(fullData);

      // Append data
      xlsx.utils.sheet_add_aoa(workSheet, fullData, {
        origin: 'A' + (topCaveat.length + 2),
      });

      // Add bottom caveat
      xlsx.utils.sheet_add_aoa(workSheet, bottomCaveat, {
        origin: 'A' + (topCaveat.length + 2 + fullData.length + 2),
      });

      xlsx.utils.book_append_sheet(workBook, workSheet);
      xlsx.writeFile(
        workBook,
        this.translateService.instant('products.capgains-excel-file-name') +
          '-' +
          dayjs().format('MMDDYYYY') +
          '.xlsx'
      );
    });
  }

  /**
   * Get maximum character of each column.
   * @param data full record for excel
   */
  private fitToColumn(data: string[][]) {
    return data[0].map((a, i) => ({
      wch: Math.max(
        ...data.map((a2) => {
          return a2[i]?.toString()?.length;
        })
      ),
    }));
  }

  /**
   * ******************** Pagination helper methods ********************
   */

  /**
   * Return total number of pages as observable.
   */
  public getNumberOfPages$(): Observable<number> {
    return this.pagedRecords.numberOfPages$;
  }

  /**
   * Return current page records as observable.
   */
  public getCurrentPage$(): Observable<CapGainsEstimatesListing[]> {
    return this.pagedRecords.currentPage$;
  }

  /**
   * Return current grid records.
   */
  public getCurrentGridRecords(): CapGainsEstimatesListing[] {
    return this.pagedRecords.records;
  }
  /**
   *
   * @returns Gives total records that are fetch in api call
   */
  public getTotalRecords(): Observable<CapGainDistribution[]> {
    return this.totalRecords$;
  }

  /**
   * Return current page number as observable.
   */
  public getCurrentPageNumber$(): Observable<number> {
    return this.pagedRecords.pageNumber$;
  }

  /**
   * Return current page number.
   */
  private getCurrentPageNumber(): number {
    return this.pagedRecords.pageNumber;
  }

  /**
   * Trigger page number selection accordingly.
   * @param n page number selected
   */
  public goToPage(n: number): void {
    if (this.pagedRecords) {
      this.pagedRecords.pageNumber = n;
    }
  }

  /**
   * Get page size for the list.
   */
  public getPageSize(): number {
    return this.pagedRecords.pageSize;
  }

  /**
   * Set new page size for the list.
   * @param size rows limit in one page.
   */
  public setPageSize(size: number): void {
    this.pagedRecords.pageSize = size;
  }
}
